"use client";

/* eslint-disable @next/next/no-img-element */

import Link from "next/link";
import {
  type PointerEvent as ReactPointerEvent,
  useEffect,
  useRef,
  useState,
} from "react";
import {
  ArrowLeft,
  ArrowRight,
  Check,
  MessageCircle,
  Pause,
  Phone,
  Play,
  ShieldCheck,
  Sparkles,
} from "lucide-react";

const AUTOPLAY_DELAY = 6800;

const heroSlides = [
  {
    id: "balcony",
    tabLabel: "Balcony nets",
    eyebrow: "Balcony safety nets",
    title: "Turn your balcony into a safer everyday space.",
    description:
      "Made-to-measure netting for apartment balconies, planned around the opening, railing, view and fixing surface.",
    desktopImage: "/carousel/balcony-safety-nets-desktop.webp",
    mobileImage: "/carousel/balcony-safety-nets-mobile.webp",
    imageAlt: "Sunlit apartment balcony enclosed with fine safety netting",
    href: "/services/balcony-safety-nets-in-bangalore",
    action: "Explore balcony nets",
  },
  {
    id: "invisible-grills",
    tabLabel: "Invisible grills",
    eyebrow: "Modern invisible grills",
    title: "Keep the skyline. Add measured protection.",
    description:
      "Slim stainless-steel cables for balconies and windows, with spacing and anchoring reviewed before installation.",
    desktopImage: "/carousel/invisible-grills-desktop.webp",
    mobileImage: "/carousel/invisible-grills-mobile.webp",
    imageAlt: "Sunlit apartment balcony with vertical stainless-steel safety cables",
    href: "/services/invisible-grills-in-bangalore",
    action: "Explore invisible grills",
  },
  {
    id: "pigeon",
    tabLabel: "Pigeon nets",
    eyebrow: "Humane pigeon protection",
    title: "A cleaner balcony without losing the breeze.",
    description:
      "Discreet exclusion netting closes the usable opening while daylight and ventilation continue through the balcony.",
    desktopImage: "/carousel/pigeon-safety-nets-desktop.webp",
    mobileImage: "/carousel/pigeon-safety-nets-mobile.webp",
    imageAlt: "Clean apartment balcony enclosed with fine pigeon netting",
    href: "/services/pigeon-safety-nets-in-bangalore",
    action: "Explore pigeon nets",
  },
  {
    id: "children",
    tabLabel: "Child safety",
    eyebrow: "Family-focused protection",
    title: "More peace of mind around little explorers.",
    description:
      "A secondary protective barrier for suitable balconies, windows and stair openings, fitted around real family routines.",
    desktopImage: "/carousel/children-safety-nets-desktop.webp",
    mobileImage: "/carousel/children-safety-nets-mobile.webp",
    imageAlt: "Parent and child seated indoors beside a netted apartment balcony",
    href: "/services/children-safety-nets-in-bangalore",
    action: "Explore child safety nets",
  },
  {
    id: "pets",
    tabLabel: "Pet safety",
    eyebrow: "Pet-friendly balconies",
    title: "A balcony your curious cat can enjoy more safely.",
    description:
      "Pet-focused netting is selected around the animal, gap size and access points, without shutting out fresh air.",
    desktopImage: "/carousel/cat-safety-nets-desktop.webp",
    mobileImage: "/carousel/cat-safety-nets-mobile.webp",
    imageAlt: "Cat resting inside a balcony enclosed with fine safety netting",
    href: "/services/cat-safety-nets-in-bangalore",
    action: "Explore cat safety nets",
  },
] as const;

const tickerItems = [
  { label: "Balcony Safety Nets", href: "/services/balcony-safety-nets-in-bangalore" },
  { label: "Pigeon Safety Nets", href: "/services/pigeon-safety-nets-in-bangalore" },
  { label: "Invisible Grills", href: "/services/invisible-grills-in-bangalore" },
  { label: "Children Safety Nets", href: "/services/children-safety-nets-in-bangalore" },
  { label: "Cat Safety Nets", href: "/services/cat-safety-nets-in-bangalore" },
];

const TickerGroup = ({ duplicate = false }: { duplicate?: boolean }) => (
  <div
    className={`flex shrink-0 items-center ${duplicate ? "service-ticker-duplicate" : ""}`}
    aria-hidden={duplicate || undefined}
  >
    {tickerItems.map((item) => (
      <Link
        key={item.label}
        href={item.href}
        tabIndex={duplicate ? -1 : undefined}
        className="flex min-h-11 shrink-0 items-center gap-4 px-5 py-3 text-xs font-extrabold tracking-[0.08em] text-white/80 uppercase transition hover:text-white sm:px-7"
      >
        <span className="size-1.5 rounded-full bg-[#FDBA74]" aria-hidden="true" />
        {item.label}
      </Link>
    ))}
    <a
      href="tel:+917995792953"
      tabIndex={duplicate ? -1 : undefined}
      className="flex min-h-11 shrink-0 items-center gap-2 px-5 py-3 text-xs font-extrabold tracking-[0.08em] text-[#BAE6FD] uppercase transition hover:text-white sm:px-7"
    >
      <Phone className="size-3.5" aria-hidden="true" />
      Call +91 79957 92953
    </a>
  </div>
);

const HeroSection = () => {
  const sectionRef = useRef<HTMLElement>(null);
  const pointerStart = useRef<{ x: number; y: number } | null>(null);
  const [current, setCurrent] = useState(0);
  const [isUserPaused, setIsUserPaused] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const [isDragging, setIsDragging] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [isDocumentVisible, setIsDocumentVisible] = useState(true);
  const [prefersReducedMotion, setPrefersReducedMotion] = useState(false);
  const [announcement, setAnnouncement] = useState("");

  const activeSlide = heroSlides[current];
  const autoplayRunning =
    !isUserPaused &&
    !isHovered &&
    !isDragging &&
    isVisible &&
    isDocumentVisible &&
    !prefersReducedMotion;

  useEffect(() => {
    const media = window.matchMedia("(prefers-reduced-motion: reduce)");
    const syncPreference = () => setPrefersReducedMotion(media.matches);
    syncPreference();
    media.addEventListener("change", syncPreference);
    return () => media.removeEventListener("change", syncPreference);
  }, []);

  useEffect(() => {
    const handleVisibility = () => setIsDocumentVisible(!document.hidden);
    handleVisibility();
    document.addEventListener("visibilitychange", handleVisibility);
    return () => document.removeEventListener("visibilitychange", handleVisibility);
  }, []);

  useEffect(() => {
    const node = sectionRef.current;
    if (!node || !("IntersectionObserver" in window)) return;

    const observer = new IntersectionObserver(
      ([entry]) => setIsVisible(entry.isIntersecting),
      { threshold: 0.2 },
    );
    observer.observe(node);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    if (!autoplayRunning) return;
    const timer = window.setTimeout(() => {
      setCurrent((slide) => (slide + 1) % heroSlides.length);
    }, AUTOPLAY_DELAY);
    return () => window.clearTimeout(timer);
  }, [autoplayRunning, current]);

  useEffect(() => {
    const nearbySlides = [
      heroSlides[(current + 1) % heroSlides.length],
      heroSlides[(current - 1 + heroSlides.length) % heroSlides.length],
    ];
    const mobile = window.matchMedia("(max-width: 639px)").matches;
    nearbySlides.forEach((slide) => {
      const preload = new window.Image();
      preload.src = mobile ? slide.mobileImage : slide.desktopImage;
    });
  }, [current]);

  const preloadSlide = (index: number) => {
    const slide = heroSlides[index];
    const mobile = window.matchMedia("(max-width: 639px)").matches;
    const preload = new window.Image();
    preload.src = mobile ? slide.mobileImage : slide.desktopImage;
  };

  const showSlide = (index: number) => {
    setCurrent(index);
    setIsUserPaused(true);
    setAnnouncement(
      `Slide ${index + 1} of ${heroSlides.length}: ${heroSlides[index].eyebrow}`,
    );
  };

  const showPrevious = () =>
    showSlide((current - 1 + heroSlides.length) % heroSlides.length);
  const showNext = () => showSlide((current + 1) % heroSlides.length);

  const handlePointerDown = (event: ReactPointerEvent<HTMLDivElement>) => {
    if ((event.target as Element).closest("a, button")) return;
    pointerStart.current = { x: event.clientX, y: event.clientY };
    event.currentTarget.setPointerCapture(event.pointerId);
    setIsDragging(true);
  };

  const handlePointerUp = (event: ReactPointerEvent<HTMLDivElement>) => {
    const start = pointerStart.current;
    pointerStart.current = null;
    if (event.currentTarget.hasPointerCapture(event.pointerId)) {
      event.currentTarget.releasePointerCapture(event.pointerId);
    }
    setIsDragging(false);
    if (!start) return;

    const deltaX = event.clientX - start.x;
    const deltaY = event.clientY - start.y;
    if (Math.abs(deltaX) < 50 || Math.abs(deltaX) <= Math.abs(deltaY) * 1.2) return;
    if (deltaX < 0) showNext();
    else showPrevious();
  };

  const toggleAutoplay = () => {
    if (prefersReducedMotion) return;
    setIsUserPaused((paused) => {
      setAnnouncement(paused ? "Automatic slide rotation resumed" : "Automatic slide rotation paused");
      return !paused;
    });
  };

  const controlClass =
    "grid size-11 shrink-0 place-items-center rounded-full border transition focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2";

  return (
    <section
      ref={sectionRef}
      className="relative overflow-hidden bg-[#F0F9FF] pt-[62px] text-[#0F172A] sm:pt-[68px] lg:bg-[#082F49] lg:text-white"
    >
      <div className="service-ticker-viewport relative z-30 overflow-hidden border-y border-white/10 bg-[#071F33]">
        <div
          className={`animate-service-ticker flex w-max hover:[animation-play-state:paused] focus-within:[animation-play-state:paused] ${
            isUserPaused ? "[animation-play-state:paused]" : ""
          }`}
        >
          <TickerGroup />
          <TickerGroup duplicate />
        </div>
      </div>

      <div
        className="relative outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#7DD3FC]"
        role="region"
        aria-roledescription="carousel"
        aria-label="Featured Servani safety services"
        tabIndex={0}
        onMouseEnter={() => setIsHovered(true)}
        onMouseLeave={() => setIsHovered(false)}
        onFocusCapture={() => setIsUserPaused(true)}
        onKeyDown={(event) => {
          if ((event.target as Element).closest("a, button")) return;
          if (event.key === "ArrowLeft") {
            event.preventDefault();
            showPrevious();
          }
          if (event.key === "ArrowRight") {
            event.preventDefault();
            showNext();
          }
        }}
      >
        <div className="pointer-events-none absolute -left-48 top-20 hidden size-[34rem] rounded-full bg-[#F59E0B]/10 blur-[120px] lg:block" />
        <div className="pointer-events-none absolute -right-52 bottom-0 hidden size-[36rem] rounded-full bg-[#0EA5E9]/12 blur-[140px] lg:block" />

        <div className="relative mx-auto grid max-w-7xl items-center gap-0 px-0 pb-10 lg:min-h-[min(700px,calc(100svh-116px))] lg:grid-cols-[.9fr_1.1fr] lg:gap-14 lg:px-10 lg:py-14 xl:gap-20">
          <div className="relative z-20 order-2 mx-4 -mt-10 rounded-[1.75rem] border border-[#BAE6FD] bg-white p-5 shadow-[0_22px_50px_rgba(3,105,161,0.13)] sm:mx-8 sm:p-7 lg:order-1 lg:mx-0 lg:mt-0 lg:rounded-none lg:border-0 lg:bg-transparent lg:p-0 lg:shadow-none">
            <div className="inline-flex items-center gap-2 rounded-full border border-[#7DD3FC] bg-[#E0F2FE] px-3 py-2 text-[11px] font-extrabold tracking-[0.12em] text-[#0369A1] uppercase lg:border-white/15 lg:bg-white/8 lg:text-[#FED7AA]">
              <Sparkles className="size-3.5" aria-hidden="true" />
              Warm homes, thoughtfully protected
            </div>

            <h1 className="mt-5 max-w-xl text-balance text-[2.25rem] leading-[1.04] font-extrabold tracking-[-0.045em] text-[#0F172A] min-[380px]:text-[2.55rem] sm:text-5xl lg:mt-6 lg:text-[3.75rem] lg:text-white xl:text-[4.25rem]">
              Safety nets and invisible grills, made for Bengaluru homes.
            </h1>

            <div
              key={activeSlide.id}
              className="animate-hero-reveal mt-6 min-h-[340px] min-[380px]:min-h-[315px] sm:min-h-[190px] lg:min-h-[230px]"
              role="group"
              aria-roledescription="slide"
              aria-label={`${current + 1} of ${heroSlides.length}`}
            >
              <p className="text-xs font-extrabold tracking-[0.13em] text-[#0369A1] uppercase lg:text-[#7DD3FC]">
                {activeSlide.eyebrow}
              </p>
              <h2 className="mt-2 max-w-xl text-balance text-2xl leading-tight font-extrabold tracking-[-0.03em] text-[#0C4A6E] sm:text-3xl lg:text-[2rem] lg:text-white">
                {activeSlide.title}
              </h2>
              <p className="mt-3 max-w-xl text-sm leading-6 text-[#475569] sm:text-base sm:leading-7 lg:text-white/72">
                {activeSlide.description}
              </p>

              <div className="mt-5 flex flex-col gap-3 sm:flex-row lg:mt-6">
                <Link
                  href={activeSlide.href}
                  className="inline-flex min-h-12 items-center justify-center gap-2 rounded-full bg-[#0369A1] px-6 py-3.5 text-sm font-extrabold text-white shadow-[0_14px_30px_rgba(3,105,161,0.24)] transition hover:-translate-y-0.5 hover:bg-[#075985] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0369A1] focus-visible:ring-offset-2 lg:ring-offset-[#082F49]"
                >
                  {activeSlide.action}
                  <ArrowRight className="size-4" aria-hidden="true" />
                </Link>
                <a
                  href="https://wa.me/917995792953"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex min-h-12 items-center justify-center gap-2 rounded-full border border-[#BAE6FD] bg-[#F0F9FF] px-6 py-3.5 text-sm font-bold text-[#075985] transition hover:bg-[#E0F2FE] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0369A1] focus-visible:ring-offset-2 lg:border-white/25 lg:bg-white/8 lg:text-white lg:hover:bg-white/14 lg:ring-offset-[#082F49]"
                >
                  <MessageCircle className="size-4" aria-hidden="true" />
                  Get a free quote
                </a>
              </div>
            </div>

            <div className="mt-3 flex flex-wrap gap-x-5 gap-y-2 text-xs font-semibold text-[#64748B] sm:text-sm lg:mt-5 lg:text-white/68">
              {["Site measurement", "Clear written scope", "24/7 enquiries"].map((item) => (
                <span key={item} className="flex items-center gap-2">
                  <span className="grid size-5 place-items-center rounded-full bg-[#E0F2FE] lg:bg-white/12">
                    <Check className="size-3 text-[#0369A1] lg:text-[#7DD3FC]" strokeWidth={3} aria-hidden="true" />
                  </span>
                  {item}
                </span>
              ))}
            </div>

            <div className="mt-7 lg:hidden">
              <div className="flex items-center justify-between gap-3">
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    onClick={showPrevious}
                    className={`${controlClass} border-[#BAE6FD] bg-white text-[#075985] hover:bg-[#E0F2FE] focus-visible:ring-[#0369A1] focus-visible:ring-offset-white`}
                    aria-label="Show previous service"
                  >
                    <ArrowLeft className="size-4" aria-hidden="true" />
                  </button>
                  <button
                    type="button"
                    onClick={toggleAutoplay}
                    disabled={prefersReducedMotion}
                    className={`${controlClass} border-[#BAE6FD] bg-white text-[#075985] hover:bg-[#E0F2FE] focus-visible:ring-[#0369A1] focus-visible:ring-offset-white disabled:cursor-not-allowed disabled:opacity-45`}
                    aria-label={
                      prefersReducedMotion
                        ? "Automatic slides disabled by reduced motion preference"
                        : isUserPaused
                          ? "Resume automatic slides"
                          : "Pause automatic slides"
                    }
                  >
                    {isUserPaused || prefersReducedMotion ? (
                      <Play className="size-4" aria-hidden="true" />
                    ) : (
                      <Pause className="size-4" aria-hidden="true" />
                    )}
                  </button>
                  <button
                    type="button"
                    onClick={showNext}
                    className={`${controlClass} border-[#BAE6FD] bg-white text-[#075985] hover:bg-[#E0F2FE] focus-visible:ring-[#0369A1] focus-visible:ring-offset-white`}
                    aria-label="Show next service"
                  >
                    <ArrowRight className="size-4" aria-hidden="true" />
                  </button>
                </div>
                <span className="text-xs font-extrabold tracking-[0.14em] text-[#64748B]">
                  {String(current + 1).padStart(2, "0")} / {String(heroSlides.length).padStart(2, "0")}
                </span>
              </div>

              <div className="mt-3 flex items-center justify-center" role="group" aria-label="Choose a service slide">
                {heroSlides.map((slide, index) => (
                  <button
                    key={slide.id}
                    type="button"
                    onClick={() => showSlide(index)}
                    onPointerEnter={() => preloadSlide(index)}
                    onFocus={() => preloadSlide(index)}
                    className="grid size-11 place-items-center rounded-full focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#0369A1] focus-visible:ring-offset-1"
                    aria-label={`Show slide ${index + 1}: ${slide.eyebrow}`}
                    aria-pressed={current === index}
                  >
                    <span
                      className={`h-2.5 rounded-full transition-all ${
                        current === index ? "w-7 bg-[#0369A1]" : "w-2.5 bg-[#CBD5E1]"
                      }`}
                      aria-hidden="true"
                    />
                  </button>
                ))}
              </div>
            </div>

            <div className="mt-8 hidden items-center gap-3 lg:flex">
              <button
                type="button"
                onClick={showPrevious}
                className={`${controlClass} border-white/20 bg-white/8 text-white hover:bg-white hover:text-[#0C4A6E] focus-visible:ring-white focus-visible:ring-offset-[#082F49]`}
                aria-label="Show previous service"
              >
                <ArrowLeft className="size-4" aria-hidden="true" />
              </button>
              <button
                type="button"
                onClick={toggleAutoplay}
                disabled={prefersReducedMotion}
                className={`${controlClass} border-white/20 bg-white/8 text-white hover:bg-white hover:text-[#0C4A6E] focus-visible:ring-white focus-visible:ring-offset-[#082F49] disabled:cursor-not-allowed disabled:opacity-45`}
                aria-label={
                  prefersReducedMotion
                    ? "Automatic slides disabled by reduced motion preference"
                    : isUserPaused
                      ? "Resume automatic slides"
                      : "Pause automatic slides"
                }
              >
                {isUserPaused || prefersReducedMotion ? (
                  <Play className="size-4" aria-hidden="true" />
                ) : (
                  <Pause className="size-4" aria-hidden="true" />
                )}
              </button>
              <button
                type="button"
                onClick={showNext}
                className={`${controlClass} border-white/20 bg-white/8 text-white hover:bg-white hover:text-[#0C4A6E] focus-visible:ring-white focus-visible:ring-offset-[#082F49]`}
                aria-label="Show next service"
              >
                <ArrowRight className="size-4" aria-hidden="true" />
              </button>
              <span className="ml-2 text-xs font-extrabold tracking-[0.15em] text-white/55">
                {String(current + 1).padStart(2, "0")} / {String(heroSlides.length).padStart(2, "0")}
              </span>
            </div>
          </div>

          <div
            className="relative order-1 mx-auto w-full touch-pan-y select-none lg:order-2 lg:max-w-none"
            onPointerDown={handlePointerDown}
            onPointerUp={handlePointerUp}
            onPointerCancel={(event) => {
              pointerStart.current = null;
              if (event.currentTarget.hasPointerCapture(event.pointerId)) {
                event.currentTarget.releasePointerCapture(event.pointerId);
              }
              setIsDragging(false);
            }}
          >
            <div className="relative aspect-[5/4] overflow-hidden bg-[#0C4A6E] lg:aspect-[4/3] lg:rounded-[2.25rem] lg:border lg:border-white/12 lg:p-2 lg:shadow-[0_35px_90px_rgba(2,6,23,0.42)]">
              <div className="relative h-full overflow-hidden lg:rounded-[1.85rem]">
                <picture key={activeSlide.id} className="animate-hero-image block h-full">
                  <source media="(max-width: 639px)" srcSet={activeSlide.mobileImage} />
                  <img
                    src={activeSlide.desktopImage}
                    alt={activeSlide.imageAlt}
                    width="1280"
                    height="960"
                    fetchPriority={current === 0 ? "high" : "auto"}
                    decoding="async"
                    draggable="false"
                    className="h-full w-full object-cover object-center"
                  />
                </picture>
                <div className="absolute inset-0 bg-gradient-to-t from-[#082F49]/75 via-transparent to-[#082F49]/12 lg:bg-gradient-to-t lg:from-[#082F49]/38 lg:via-transparent lg:to-transparent" />

                <div className="absolute left-5 top-5 flex items-center gap-2 rounded-full border border-white/30 bg-[#082F49]/70 px-3 py-2 text-[11px] font-extrabold tracking-[0.12em] text-white backdrop-blur-sm lg:left-6 lg:top-6">
                  <ShieldCheck className="size-4 text-[#7DD3FC]" aria-hidden="true" />
                  {String(current + 1).padStart(2, "0")} · {activeSlide.tabLabel}
                </div>

                <div className="absolute inset-x-5 bottom-14 flex items-end justify-between gap-4 lg:inset-x-6 lg:bottom-6">
                  <div className="rounded-2xl border border-white/25 bg-[#FFFDF8]/94 px-4 py-3 text-[#0F172A] shadow-xl lg:max-w-[260px]">
                    <p className="text-sm font-extrabold">Made to measure</p>
                    <p className="mt-1 hidden text-xs leading-5 text-[#475569] sm:block">
                      Opening, access and fixing surface checked before confirmation.
                    </p>
                  </div>
                  <div className="hidden items-center gap-3 rounded-2xl border border-white/25 bg-[#082F49]/78 px-4 py-3 text-white shadow-xl backdrop-blur-sm lg:flex">
                    <Phone className="size-4 text-[#7DD3FC]" aria-hidden="true" />
                    <div>
                      <p className="text-[10px] tracking-wider text-white/60 uppercase">Enquiries</p>
                      <p className="text-sm font-extrabold">Open 24/7</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>
            <p className="absolute bottom-3 left-1/2 -translate-x-1/2 text-[10px] font-bold tracking-[0.12em] text-white/70 uppercase lg:hidden">
              Swipe to explore
            </p>
          </div>
        </div>

        <div className="relative hidden border-t border-white/10 bg-[#071F33]/72 lg:block">
          <div className="mx-auto grid max-w-7xl grid-cols-5 px-10">
            {heroSlides.map((slide, index) => (
              <button
                key={slide.id}
                type="button"
                onClick={() => showSlide(index)}
                onPointerEnter={() => preloadSlide(index)}
                onFocus={() => preloadSlide(index)}
                className={`group relative min-h-[92px] border-x border-white/6 px-5 py-4 text-left transition focus-visible:z-10 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-[#7DD3FC] ${
                  current === index ? "bg-white/10" : "hover:bg-white/6"
                }`}
                aria-label={`Show slide ${index + 1}: ${slide.eyebrow}`}
                aria-pressed={current === index}
              >
                <span
                  className={`absolute inset-x-0 top-0 h-0.5 transition ${
                    current === index ? "bg-[#FDBA74]" : "bg-transparent group-hover:bg-white/25"
                  }`}
                />
                <span className="text-[10px] font-extrabold tracking-[0.16em] text-[#7DD3FC]">
                  {String(index + 1).padStart(2, "0")}
                </span>
                <span className="mt-1 block text-sm font-extrabold text-white">{slide.tabLabel}</span>
              </button>
            ))}
          </div>
        </div>

        <p className="sr-only" aria-live="polite" aria-atomic="true">
          {announcement}
        </p>
      </div>
    </section>
  );
};

export default HeroSection;
